import React, { useEffect, useState, useRef } from 'react';
import { tenantAPI } from '../../lib/api';
import { formatCurrency, formatDate, getStatusColor, getPaymentMethodColor, getCurrentMonth, generateMonthOptions } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { 
  CreditCard, Plus, Upload, Loader2, Smartphone, Building2, Banknote,
  Copy, CheckCircle, Info, ChevronDown, ChevronUp, Phone, Mail, User
} from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';

const PAYMENT_METHOD_ICONS = {
  gcash: { icon: Smartphone, color: 'bg-blue-500', label: 'GCash' },
  maya: { icon: Smartphone, color: 'bg-green-500', label: 'Maya' },
  diskartech: { icon: Smartphone, color: 'bg-orange-500', label: 'Diskartech' },
  palawan: { icon: Building2, color: 'bg-yellow-500', label: 'Palawan Express' },
  cash: { icon: Banknote, color: 'bg-emerald-500', label: 'Cash' },
  bank: { icon: Building2, color: 'bg-purple-500', label: 'Bank Transfer' },
};

// Ensure landlord-entered links open as absolute URLs
const PAYMENT_TYPES = [
  { value: 'rent', label: 'Monthly Rent' },
  { value: 'deposit', label: 'Deposit' },
  { value: 'utility', label: 'Utility' },
  { value: 'other', label: 'Other Fees' },
];

export default function TenantPayments() {
  const { t } = useLanguage();
  const [payments, setPayments] = useState([]);
  const [lease, setLease] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [paymentInfoOpen, setPaymentInfoOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [landlordPaymentMethods, setLandlordPaymentMethods] = useState(null);
  const [expandedMethod, setExpandedMethod] = useState(null);
  const [copiedField, setCopiedField] = useState(null);
  const fileInputRef = useRef(null);
  const [formData, setFormData] = useState({
    amount: '',
    payment_type: 'rent',
    payment_method: '',
    period_month: getCurrentMonth(),
    payment_date: new Date().toISOString().split('T')[0],
    reference_number: '',
    notes: '',
    proof_file: null,
  });
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [paymentsRes, leaseRes, paymentMethodsRes] = await Promise.all([
        tenantAPI.getPayments(),
        tenantAPI.getLease(),
        tenantAPI.getLandlordPaymentMethods().catch(() => ({ data: null })),
      ]);
      setPayments(paymentsRes.data);
      setLease(leaseRes.data);
      setLandlordPaymentMethods(paymentMethodsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredPayments = activeTab === 'all'
    ? payments
    : payments.filter(p => p.status === activeTab);

  const handleOpenDialog = () => {
    if (!lease) {
      toast.error('No active lease found');
      return;
    }
    setFormData({
      amount: lease.total_monthly_rent?.toString() || lease.monthly_rent?.toString() || '',
      payment_type: 'rent',
      payment_method: '',
      period_month: getCurrentMonth(),
      payment_date: new Date().toISOString().split('T')[0],
      reference_number: '',
      notes: '',
      proof_file: null,
    });
    setPreviewUrl(null);
    setExpandedMethod(null);
    setDialogOpen(true);
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (file) {
      setFormData({ ...formData, proof_file: file });
      setPreviewUrl(URL.createObjectURL(file));
    }
  };

  const handleCopy = async (text, field) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedField(field);
      toast.success('Copied to clipboard');
      setTimeout(() => setCopiedField(null), 2000);
    } catch (err) {
      toast.error('Failed to copy');
    }
  };

  const handleSubmit = async () => {
    if (!formData.payment_method || !formData.proof_file) {
      toast.error('Please fill in all required fields and upload proof of payment');
      return;
    }

    setSaving(true);
    try {
      const data = new FormData();
      data.append('lease_id', lease.lease_id);
      data.append('amount', formData.amount);
      data.append('payment_type', formData.payment_type);
      data.append('payment_method', formData.payment_method);
      data.append('period_month', formData.period_month);
      if (formData.payment_date) data.append('payment_date', formData.payment_date);
      if (formData.reference_number) data.append('reference_number', formData.reference_number);
      if (formData.notes) data.append('notes', formData.notes);
      data.append('proof_file', formData.proof_file);

      await tenantAPI.submitPayment(data);
      toast.success('Payment submitted successfully!');
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit payment');
    } finally {
      setSaving(false);
    }
  };

  // Filter out null/disabled payment methods
  const allMethods = landlordPaymentMethods?.payment_methods || {};
  const enabledMethods = Object.fromEntries(
    Object.entries(allMethods).filter(([key, config]) => key !== 'online_payment_link' && config && config.enabled)
  );
  const hasPaymentMethods = Object.keys(enabledMethods).length > 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="tenant-payments-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl lg:text-3xl font-bold">Payments</h1>
          <p className="text-muted-foreground mt-1">Submit and track your payments</p>
        </div>
        <div className="flex gap-2">
          {hasPaymentMethods && (
            <Button
              variant="outline"
              onClick={() => setPaymentInfoOpen(true)}
              data-testid="view-payment-info-btn"
            >
              <Info className="w-4 h-4 mr-2" />
              Payment Info
            </Button>
          )}
          <Button
            onClick={handleOpenDialog}
            className="bg-[#6B8E6B] hover:bg-[#5A7A5A]"
            disabled={!lease}
            data-testid="submit-payment-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Submit Payment
          </Button>
        </div>
      </div>

      {/* Payment Methods Info Card */}
      {hasPaymentMethods && (
        <Card className="bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium text-blue-900">Where to pay?</p>
                <p className="text-sm text-blue-700">
                  Your landlord has set up {Object.keys(enabledMethods).length} payment method(s). 
                  Click "Payment Info" or open "Submit Payment" to view account details.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="all">All ({payments.length})</TabsTrigger>
          <TabsTrigger value="pending">Pending ({payments.filter(p => p.status === 'pending').length})</TabsTrigger>
          <TabsTrigger value="verified">Verified ({payments.filter(p => p.status === 'verified').length})</TabsTrigger>
          <TabsTrigger value="rejected">Rejected ({payments.filter(p => p.status === 'rejected').length})</TabsTrigger>
        </TabsList>

        <TabsContent value={activeTab} className="mt-6">
          {filteredPayments.length === 0 ? (
            <Card>
              <CardContent className="py-16 text-center">
                <div className="w-16 h-16 rounded-full bg-muted flex items-center justify-center mx-auto mb-4">
                  <CreditCard className="w-8 h-8 text-muted-foreground" />
                </div>
                <h3 className="font-semibold text-lg mb-2">No {activeTab} payments</h3>
                <p className="text-muted-foreground mb-4">
                  {lease ? 'Submit your payment to get started' : 'You need an active lease to submit payments'}
                </p>
                {lease && (
                  <Button onClick={handleOpenDialog} className="bg-[#6B8E6B] hover:bg-[#5A7A5A]">
                    <Plus className="w-4 h-4 mr-2" />
                    Submit Payment
                  </Button>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {filteredPayments.map((payment, index) => (
                <Card key={payment.payment_id} className="animate-fade-in" style={{ animationDelay: `${index * 0.03}s` }}>
                  <CardContent className="p-6">
                    <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <span className="text-2xl font-bold">{formatCurrency(payment.amount)}</span>
                          <Badge className={getPaymentMethodColor(payment.payment_method)}>
                            {payment.payment_method?.toUpperCase()}
                          </Badge>
                        </div>
                        <div className="flex flex-wrap gap-4 text-sm text-muted-foreground">
                          <span className="capitalize">{payment.payment_type}</span>
                          <span>Period: {payment.period_month}</span>
                          <span>Submitted: {formatDate(payment.created_at)}</span>
                        </div>
                        {payment.notes && (
                          <p className="text-sm mt-2">{payment.notes}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-3">
                        <Badge className={getStatusColor(payment.status)}>{payment.status}</Badge>
                        {payment.receipt_number && (
                          <span className="text-xs font-mono text-muted-foreground">
                            {payment.receipt_number}
                          </span>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      {/* Submit Payment Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Submit Payment</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto pr-2">
            {/* Landlord Payment Methods Section */}
            {hasPaymentMethods && (
              <Card className="bg-blue-50/50 dark:bg-blue-950/50 border-blue-200 dark:border-blue-800">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm font-medium text-blue-900 flex items-center gap-2">
                    <Info className="w-4 h-4" />
                    Where to Send Payment
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-2">
                  {Object.entries(enabledMethods).map(([method, config]) => {
                    const methodInfo = PAYMENT_METHOD_ICONS[method];
                    if (!methodInfo) return null;
                    const Icon = methodInfo.icon;
                    const isExpanded = expandedMethod === method;
                    
                    return (
                      <div key={method} className="bg-white dark:bg-gray-800 rounded-lg border dark:border-gray-700">
                        <button
                          onClick={() => setExpandedMethod(isExpanded ? null : method)}
                          className="w-full p-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                        >
                          <div className="flex items-center gap-3">
                            <div className={`w-8 h-8 rounded-lg ${methodInfo.color} flex items-center justify-center`}>
                              <Icon className="w-4 h-4 text-white" />
                            </div>
                            <span className="font-medium text-sm">{methodInfo.label}</span>
                          </div>
                          {isExpanded ? (
                            <ChevronUp className="w-4 h-4 text-muted-foreground" />
                          ) : (
                            <ChevronDown className="w-4 h-4 text-muted-foreground" />
                          )}
                        </button>
                        
                        {isExpanded && (
                          <div className="px-3 pb-3 space-y-2 border-t dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/50">
                            <div className="pt-2 space-y-2">
                              {method === 'bank' ? (
                                <>
                                  {config.bank_name && (
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-muted-foreground">Bank:</span>
                                      <span className="font-medium">{config.bank_name}</span>
                                    </div>
                                  )}
                                  {config.branch && (
                                    <div className="flex items-center justify-between text-sm">
                                      <span className="text-muted-foreground">Branch:</span>
                                      <span className="font-medium">{config.branch}</span>
                                    </div>
                                  )}
                                </>
                              ) : null}
                              
                              {config.account_name && (
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-muted-foreground">Account Name:</span>
                                  <span className="font-medium">{config.account_name}</span>
                                </div>
                              )}
                              
                              {config.account_number && (
                                <div className="flex items-center justify-between text-sm">
                                  <span className="text-muted-foreground">
                                    {method === 'cash' ? 'Contact:' : 'Account #:'}
                                  </span>
                                  <div className="flex items-center gap-2">
                                    <span className="font-mono font-medium">{config.account_number}</span>
                                    <button
                                      onClick={() => handleCopy(config.account_number, `${method}-number`)}
                                      className="p-1 hover:bg-gray-200 rounded"
                                    >
                                      {copiedField === `${method}-number` ? (
                                        <CheckCircle className="w-3.5 h-3.5 text-green-500" />
                                      ) : (
                                        <Copy className="w-3.5 h-3.5 text-muted-foreground" />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              )}
                              
                              {config.instructions && (
                                <div className="text-sm pt-1 border-t mt-2">
                                  <span className="text-muted-foreground block mb-1">Instructions:</span>
                                  <p className="text-xs bg-amber-50 dark:bg-amber-950 p-2 rounded border border-amber-200 dark:border-amber-800">{config.instructions}</p>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            <Separator />

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Payment Type</Label>
                <Select
                  value={formData.payment_type}
                  onValueChange={(v) => setFormData({ ...formData, payment_type: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_TYPES.map((type) => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Period</Label>
                <Select
                  value={formData.period_month}
                  onValueChange={(v) => setFormData({ ...formData, period_month: v })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {generateMonthOptions().map((month) => (
                      <SelectItem key={month.value} value={month.value}>
                        {month.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="amount">Amount (₱) *</Label>
              <Input
                id="amount"
                type="text"
                value={formData.amount}
                onChange={(e) => setFormData(prev => ({ ...prev, amount: e.target.value }))}
                data-testid="payment-amount-input"
              />
            </div>

            <div className="space-y-2">
              <Label>Payment Method *</Label>
              <Select
                value={formData.payment_method}
                onValueChange={(v) => setFormData({ ...formData, payment_method: v })}
              >
                <SelectTrigger data-testid="payment-method-select">
                  <SelectValue placeholder="Select payment method" />
                </SelectTrigger>
                <SelectContent>
                  {hasPaymentMethods ? (
                    Object.keys(enabledMethods).map((method) => {
                      const methodInfo = PAYMENT_METHOD_ICONS[method];
                      if (!methodInfo) return null;
                      return (
                        <SelectItem key={method} value={method}>
                          {methodInfo.label}
                        </SelectItem>
                      );
                    })
                  ) : (
                    <>
                      <SelectItem value="gcash">GCash</SelectItem>
                      <SelectItem value="maya">Maya</SelectItem>
                      <SelectItem value="diskartech">Diskartech</SelectItem>
                      <SelectItem value="palawan">Palawan Express</SelectItem>
                      <SelectItem value="bank">Bank Transfer</SelectItem>
                      <SelectItem value="cash">Cash</SelectItem>
                    </>
                  )}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label htmlFor="payment-date">Payment Date *</Label>
                <Input
                  id="payment-date"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, payment_date: e.target.value }))}
                  data-testid="payment-date-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="reference-number">Reference Number</Label>
                <Input
                  id="reference-number"
                  value={formData.reference_number}
                  onChange={(e) => setFormData(prev => ({ ...prev, reference_number: e.target.value }))}
                  placeholder="e.g. 0053-1234-5678"
                  data-testid="payment-reference-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="notes">Notes (Optional)</Label>
              <Input
                id="notes"
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                placeholder="Any additional details"
              />
            </div>

            <div className="space-y-2">
              <Label>Proof of Payment *</Label>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleFileChange}
                className="hidden"
              />
              <div
                onClick={() => fileInputRef.current?.click()}
                className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:bg-muted/50 transition-colors"
              >
                {previewUrl ? (
                  <div className="space-y-2">
                    <img
                      src={previewUrl}
                      alt="Preview"
                      className="max-h-[200px] mx-auto rounded-lg object-contain"
                    />
                    <p className="text-sm text-muted-foreground">Click to change</p>
                  </div>
                ) : (
                  <>
                    <Upload className="w-10 h-10 text-muted-foreground mx-auto mb-2" />
                    <p className="text-sm text-muted-foreground">
                      Click to upload screenshot of your payment
                    </p>
                  </>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-[#6B8E6B] hover:bg-[#5A7A5A]"
              data-testid="confirm-payment-btn"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Submit Payment
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Info Dialog */}
      <Dialog open={paymentInfoOpen} onOpenChange={setPaymentInfoOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="w-5 h-5 text-[#C85A42]" />
              Payment Information
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Landlord Info */}
            {landlordPaymentMethods?.landlord && (
              <Card className="bg-muted/30">
                <CardContent className="p-4">
                  <p className="text-sm text-muted-foreground mb-2">Pay to:</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-[#2F4F4F] flex items-center justify-center text-white font-bold">
                      {landlordPaymentMethods.landlord.name?.charAt(0) || 'L'}
                    </div>
                    <div>
                      <p className="font-medium">{landlordPaymentMethods.landlord.name}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="w-3 h-3" />
                        {landlordPaymentMethods.landlord.email}
                      </div>
                      {landlordPaymentMethods.landlord.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="w-3 h-3" />
                          {landlordPaymentMethods.landlord.phone}
                        </div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Payment Methods */}
            <div className="space-y-3">
              <p className="text-sm font-medium">Available Payment Methods:</p>
              {Object.entries(enabledMethods).map(([method, config]) => {
                const methodInfo = PAYMENT_METHOD_ICONS[method];
                if (!methodInfo) return null;
                const Icon = methodInfo.icon;
                
                return (
                  <Card key={method}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3 mb-3">
                        <div className={`w-10 h-10 rounded-lg ${methodInfo.color} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <span className="font-semibold">{methodInfo.label}</span>
                      </div>
                      
                      <div className="space-y-2 text-sm">
                        {method === 'bank' && config.bank_name && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Bank:</span>
                            <span className="font-medium">{config.bank_name}</span>
                          </div>
                        )}
                        {method === 'bank' && config.branch && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Branch:</span>
                            <span className="font-medium">{config.branch}</span>
                          </div>
                        )}
                        {config.account_name && (
                          <div className="flex justify-between">
                            <span className="text-muted-foreground">Account Name:</span>
                            <span className="font-medium">{config.account_name}</span>
                          </div>
                        )}
                        {config.account_number && (
                          <div className="flex justify-between items-center">
                            <span className="text-muted-foreground">
                              {method === 'cash' ? 'Contact:' : 'Account #:'}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="font-mono font-medium">{config.account_number}</span>
                              <button
                                onClick={() => handleCopy(config.account_number, `info-${method}`)}
                                className="p-1 hover:bg-muted rounded"
                              >
                                {copiedField === `info-${method}` ? (
                                  <CheckCircle className="w-4 h-4 text-green-500" />
                                ) : (
                                  <Copy className="w-4 h-4 text-muted-foreground" />
                                )}
                              </button>
                            </div>
                          </div>
                        )}
                        {config.instructions && (
                          <div className="pt-2 border-t mt-2">
                            <p className="text-muted-foreground mb-1">Instructions:</p>
                            <p className="bg-amber-50 dark:bg-amber-950 p-2 rounded text-xs border border-amber-200 dark:border-amber-800 text-amber-900 dark:text-amber-200">{config.instructions}</p>
                          </div>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
